import { Injectable } from '@angular/core';
import { environment } from "../../../environments/environment";
import { HttpClient } from '@angular/common/http';
import { take } from 'rxjs/operators';
import { AppNotification, AppNotificationBase, DeepLinkType, DownloadAppQuery, JoiApp, JoiAppData, JoiAppInfo, SurveySubmissionData, standardizeAppInfo } from 'globals/store/app.data';
import { loadLocal, saveLocal, sleepForMS } from '../../helpers/helpers';
import { Subject } from 'rxjs';
import { MobileAppState, mobileSubCallback, mobileSubTilDestroy } from './mobile-store/mobile.app.store';
import { Store, select } from '@ngrx/store';
import { SubscriptionHelper } from '../../services/helpers/subscriptions.helper';
import { transformObservableToSinglePromise } from '../../helpers/angular-helpers';
import { Router } from '@angular/router';
import { MobileAppSubpage } from './mobile-store/mobile.data';
import { appsActions } from 'src/app/store/app.era';
import { MobileAppActions, mobileAppFeature } from './mobile-store/mobile.app.era';
import { collection, doc, DocumentChange, DocumentData, DocumentSnapshot, Firestore, Query, query } from '@angular/fire/firestore';
import { where } from '@angular/fire/firestore';

declare global {
  interface Window {
    fromFlutter?: boolean;
    setFromFlutter?: any;
    receiveDebugMessageFromFlutter?: any;
    receiveAppDatasFromFlutter?: any;
    receiveNotificationFromFlutter?: any;
    openedWithNotificationFromFlutter?: any;
    openURLInFlutter?: any;
    deepLink?: any;
    openURL?: any;
    fakeLoginApp?: any;
    sendDebugMessageToFlutter?: any;
    requestAppDatasFromFlutter?: any;
    sendAppDatasToFlutter?: any;
  }
}

const GlobalFunctionsOrPropsDefinedInIndexHTML: (keyof globalThis.Window)[] = [
  'setFromFlutter',
  'receiveDebugMessageFromFlutter',
  'receiveAppDatasFromFlutter',
  'receiveNotificationFromFlutter',
  'openedWithNotificationFromFlutter',
  'deepLink',
  'openURL'
]
const GlobalFunctionsOrPropsDefinedInFlutter: (keyof globalThis.Window)[] = [
  'sendDebugMessageToFlutter',
  'requestAppDatasFromFlutter',
]

const DEFAULT_FLUTTER_INITIALIZATION_TIME = 5000
const FlutterInitializationTime = environment.hasOwnProperty('flutterLoadingTime') ? environment['flutterLoadingTime'] : DEFAULT_FLUTTER_INITIALIZATION_TIME // Need to be safe, bumping up to 5000.  The environmental variable is new for faster development in local dev

@Injectable({ providedIn: 'root' })
export class MobileAppService { 

  readonly api = `${environment.functionsURL}api/v1`;
  readonly _file = 'mobile.service.ts'
  subscribedId: string = null
  version: number = null
  loadingAppDatas: boolean
  appDatas: JoiAppData[]
  flutterGivenTimeToInitialize: boolean

  appData: JoiAppData
  $destroy: Subject<boolean> = new Subject() // should never be invoked
  $app_destroy: Subject<boolean> = new Subject()
  $reset_notifications: Subject<boolean> = new Subject()


  receiveAppDatasFromFlutter(appDatas: string) {
    this.store.dispatch(MobileAppActions.flutterInitialized())
    this.loadingAppDatas = false
    console.log("Receiving appDatas: ", appDatas)
    if (!appDatas) this.appDatas = []
    else this.appDatas = JSON.parse(appDatas) 
  }

  deepLink(deepLinkType: DeepLinkType, id: string) {
    console.log(`deepLink called, deepLinkType: ${deepLinkType}, id: ${id}`)
    switch (deepLinkType) {
      case 'login':
        this.router.navigate(['login', id ])
        break
      default:
        console.log("No handling for deep link type: " + deepLinkType)
        break
    }
  }

  receiveNotificationFromFlutter(appNotificationString: string) {
    const notification = JSON.parse(appNotificationString) as AppNotification
    console.log("Received notification string: " + appNotificationString)
    this.store.dispatch(MobileAppActions.showNotification({payload: notification}))
  }

  async openedWithNotificationFromFlutter(appNotificationString: string) {
    const notification = JSON.parse(appNotificationString) as AppNotification;
    console.log("opened with notification from flutter, linkType: " + notification.linkType);
    if (notification.linkType) this.openNotificationLink(notification);
  }

  async checkForNoFlutter() {
    await this.giveFlutterTimeToInitialize()
    if (!window.fromFlutter) this.store.dispatch(MobileAppActions.flutterInitialized())
    if (window.fromFlutter) {
      GlobalFunctionsOrPropsDefinedInFlutter.forEach(functionOrProp => {
        if (!window.hasOwnProperty(functionOrProp)) console.log(`Missing FLUTTER ${functionOrProp} from the global window!`) 
      }) 
    }
    return
  }
  
  constructor(
    private http:HttpClient,
    private subscriptionHelper: SubscriptionHelper,
    public store: Store<MobileAppState>,
    private db: Firestore,
    private router: Router
  ) {

    // First, bind any necessary callbacks to the globalThis / window.  Even if not in flutter web view, do this (in fact it's more important because it's easier to see the logs in webview)
    window.receiveAppDatasFromFlutter = this.receiveAppDatasFromFlutter.bind(this);
    window.receiveNotificationFromFlutter = this.receiveNotificationFromFlutter.bind(this);
    window.openedWithNotificationFromFlutter = this.openedWithNotificationFromFlutter.bind(this);
    window.openURL = this.openURL.bind(this);
    window.deepLink = this.deepLink.bind(this);
    this.checkForNoFlutter();

    setInterval(() => this.store.dispatch(MobileAppActions.loadTime()), 10000); // timer function to determine which notifications / surveys to show
    mobileSubTilDestroy(this, this.store.select(mobileAppFeature.selectInitialized), 'flutterGivenTimeToInitialize')

    // Next, make sure all of the correct functions exist in the index HTML file
    GlobalFunctionsOrPropsDefinedInIndexHTML.forEach(functionOrProp => {
      if (!window.hasOwnProperty(functionOrProp)) console.log(`Missing ${functionOrProp} from the global window!`)
    })    
    
    mobileSubCallback(this.$destroy, this.store.select(mobileAppFeature.selectId), id => {
      console.log(`App Id in mobile sub callback: ${id}`)
      if (!!id && this.subscribedId !== id) {
        console.log("Changing id")
        this.$app_destroy.next(true)
        this.$reset_notifications.next(true)
        mobileSubTilDestroy(this, this.store.select(mobileAppFeature.selectAppData), 'appData', this.$app_destroy)
        this.subscribedId = id
        this.subscribeToApp(id)
      } else if (!id) this.subscribedId = null
    })
  } // DON'T INITIALIZE OTHER APP SERVICES IN THIS SERVICE!!!

  changeSubpage(subPage: MobileAppSubpage, appId?: string) {
    this.router.navigate(['app', appId || this.subscribedId, subPage ])
  }

  openURL(url: string) { // all links need to go through this.  Will probably mean having to reworking the publish stuff...
    if (window.fromFlutter) window.openURLInFlutter.postMessage(url)
    else window.open(url, '_blank')
  }

  openNotificationLink<T extends AppNotificationBase>(notification: T) {
    console.log("Opening notification link...", notification.linkType)
    if (notification.linkType === 'URL') {
      this.openURL(notification.linkUrl)
    } else this.changeSubpage(notification.linkType, notification.appId)
    this.closeNotification(notification)
  }

  async closeNotification<T extends AppNotificationBase>(notification: T) {
    const appData = await this.loadLocalAppDataById(notification.appId)
    if (!appData) console.log(`Missing app data with id: ${notification.appId}!`)
    else {
      appData.archivedNotificationIds.push(notification.id)
      this.saveAppData(appData)
    }
    this.store.dispatch(MobileAppActions.closeNotification())
  }

  async giveFlutterTimeToInitialize() {
    let remainingTime = FlutterInitializationTime
    while (!this.flutterGivenTimeToInitialize && remainingTime > 0) {
      if (window.fromFlutter) break
      remainingTime -= 100
      await sleepForMS(100)
    }
  }

  async subscribeToApp(id: string) {
    console.log("SUBSCRIBE TO APP: " + id)
    const changesHandler = async(changes: DocumentSnapshot<JoiAppInfo>) => {
      let appInfo: JoiAppInfo
      if (!changes.exists) {
        console.log("NO APP!")
        this.exitApp()
        this.store.dispatch(MobileAppActions.setAppNotFound({ payload: true }))
        return
      } else appInfo = changes.data()
      standardizeAppInfo(appInfo)
      this.store.dispatch(MobileAppActions.loadAppInfo({ payload: appInfo }))
      const publishCodeChanged = this.appData?.publishCode !== appInfo.publishCode
      if (publishCodeChanged) {
        console.log("App data publish code !== appInfo publish code", { appData: this.appData, appInfo })
        let appData = await this.loadLocalAppDataByPublishCode(appInfo.publishCode)
        if (!appData) {
          console.error("MISSING APP DATA! SHOULD BE THERE!") 
          const { id, publishCode, passwordProtected, nameAndEmail, title } = appInfo
          appData = { title, id, publishCode, passwordProtected, nameAndEmail, completedSurveyIds: [], archivedNotificationIds: [] }
        }
        // console.log(appData)
        // console.log({ appInfo, appData, thisAppData: this.appData })
        this.store.dispatch(MobileAppActions.loadAppData({ payload: appData }))
      }
      // now to get the static data if missing or different app
      if (publishCodeChanged || (!this.version || this.version < appInfo.version)) {
        this.version = appInfo.version
        this.downloadStaticAppData(appInfo)
      }
    }
    this.subscriptionHelper.subscribeToDocumentSnapshotChanges(doc(this.db, `publicApps/${id}`), 
      changesHandler.bind(this), this.$app_destroy, this._file, 'subscribeToApp')

    this.subscribeToAppNotifications(id)
    await this.giveFlutterTimeToInitialize()
    if (window.fromFlutter) window.fakeLoginApp.postMessage(id)
  }

  async subscribeToAppNotifications(appId: string) {
    this.$reset_notifications.next(true)
    const notificationsCollection = collection(this.db, 'appNotifications')
    const notificationsQuery: Query<any, DocumentData> = query(notificationsCollection, where('appId', '==', appId))
    this.subscriptionHelper.subscribeToCollectionsWithHandlerAndStopper<AppNotification>(notificationsQuery, 'appNotifications', this.handleNotificationsFromDB.bind(this), this.$reset_notifications, this._file, 'getEventApps - notifications')
  }

  async handleNotificationsFromDB(changes:DocumentChange<AppNotification>[]){
    // console.log(`${changes.length} Notification changes received from database`)
    const toDelete = changes.filter(change => change.type === 'removed').map(change => change.doc.data())
    const toAddOrUpdate = changes.filter(change => change.type !== 'removed').map(change => change.doc.data())
    if (toDelete.length) this.store.dispatch(appsActions.removeAppNotifications(toDelete))
    if (toAddOrUpdate.length) this.store.dispatch(appsActions.loadAppNotifications(toAddOrUpdate))
  }

  async downloadStaticAppData(appInfo: JoiAppInfo) {
    const downloadQuery: DownloadAppQuery = {
      id: appInfo.id,
      eventId: appInfo.eventId
    }
    const url = `${this.api}/load-static-app`
    const joiApp = await transformObservableToSinglePromise<JoiApp>(this.http.post(url, downloadQuery))
    console.log({ url, joiApp })
    this.store.dispatch(MobileAppActions.loadApp({ payload: joiApp }))
  }


  async submitSurvey(submitData: SurveySubmissionData): Promise<boolean> {
    try {
      const url = `${this.api}/submit-survey`
      await this.http.post(url, submitData).pipe(take(1)).toPromise()
      return true
    } catch (response) {
      console.log(response)
      return false
    }
  }
  
  async loadAppData(publishCode: string, password: string = ''): Promise<JoiAppData> {
    const url = `${this.api}/load-app-data`
    const query = { publishCode, password }
    console.log(`Load app data called with publishCode: ${publishCode}, password: ${password}`)
    const appData = await this.http.post(url, query).pipe(take(1)).toPromise()
    return standardizeAppData(appData as JoiAppData)
  }

  async loadAppDatas(): Promise<JoiAppData[]> {
    console.log(`fromFlutter: ${window.fromFlutter}, appDatas: ${JSON.stringify(this.appDatas)}`)
    if (this.appDatas) return this.appDatas
    await this.giveFlutterTimeToInitialize()

    if (window.fromFlutter) {
      while (!window.requestAppDatasFromFlutter?.postMessage) {
        console.log("Flutter function not found / initialized")
        await sleepForMS(500)
      }
      this.loadingAppDatas = true
      window.requestAppDatasFromFlutter.postMessage('GIVE ME SOME APPS')
      while (this.loadingAppDatas) {
        console.log('loading...')
        await sleepForMS(50)
        if (!this.loadingAppDatas) break
      }
      return this.appDatas
    } else {
      const appDatas = loadLocal('apps', [])
      this.appDatas = appDatas
      return appDatas
    }
  }

  
  async saveAppDatas(appDatas: JoiAppData[]) {
    this.appDatas = appDatas
    if (window.fromFlutter) window.sendAppDatasToFlutter.postMessage(JSON.stringify(appDatas))
    else saveLocal('apps', appDatas)
  }

  async removeAppById(id: string) {
    let appDatas =  await this.loadAppDatas()
    appDatas = appDatas.filter(a => a.id !== id)
    this.saveAppDatas(appDatas)
  }

  async saveAppData(appData: JoiAppData) {
    const appDatas =  await this.loadAppDatas()
    const index = appDatas.findIndex(a => a.publishCode === appData.publishCode)
    if (index === -1) appDatas.push(appData)
    else {
      appDatas[index] = appData
    }
    this.saveAppDatas(appDatas)
  }


  async loadLocalAppDataById(appId: string): Promise<JoiAppData> {
    const localAppDatas = await this.loadAppDatas()
    const localAppData = localAppDatas.find(a => a.id === appId)
    return standardizeAppData(localAppData) || null
  }

  async loadLocalAppDataByPublishCode(publishCode: string): Promise<JoiAppData> {
    const localAppDatas = await this.loadAppDatas()
    const localAppData = localAppDatas.find(a => a.publishCode === publishCode)
    return standardizeAppData(localAppData) || null
  }

  exitApp() {
    this.store.dispatch(MobileAppActions.loadAppId({ payload: null }))
    this.store.dispatch(MobileAppActions.loadAppInfo({ payload: null }))
    this.store.dispatch(MobileAppActions.loadAppData({ payload: null }))
    this.router.navigate(['/'])
  }
}


const standardizeAppData = (appData: JoiAppData): JoiAppData => {
  if (!appData) return null
  if (!appData.completedSurveyIds) appData.completedSurveyIds = []
  if (!appData.archivedNotificationIds) appData.archivedNotificationIds = []
  if (!appData.selectedSessionIds) appData.selectedSessionIds = []
  return appData
}